import axios from 'axios';
import config from '../../config';

const API_TIMEOUT_MS = parseInt(process.env.REACT_APP_API_TIMEOUT_MS, 10) || 180000;

const apiClient = axios.create({
  baseURL: config.api,
  // Image generation/edit flows can take longer than normal API requests.
  timeout: API_TIMEOUT_MS,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

let unauthorizedHandler = null;
let sessionRefreshHandler = null;

function isAuthRequest(config = {}) {
  const requestUrl = config.url || '';
  return requestUrl.indexOf('/auth/') !== -1;
}

function getHeader(headers, name) {
  if (!headers) {
    return null;
  }

  if (typeof headers.get === 'function') {
    return headers.get(name) || headers.get(name.toLowerCase());
  }

  return headers[name] || headers[name.toLowerCase()];
}

function getRequestAuthHeader(requestConfig = {}) {
  const requestHeaders = requestConfig.headers || {};
  return (
    getHeader(requestHeaders, 'Authorization')
    || apiClient.defaults.headers.common.Authorization
    || null
  );
}

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error && error.response && error.response.status;
    const requestConfig = error && error.config;
    const hasAuthHeader = Boolean(getRequestAuthHeader(requestConfig));

    if (status === 401 && hasAuthHeader && !isAuthRequest(requestConfig) && unauthorizedHandler) {
      unauthorizedHandler(error);
    }

    return Promise.reject(error);
  },
);

apiClient.interceptors.request.use(async (requestConfig) => {
  if (
    sessionRefreshHandler
    && requestConfig
    && !isAuthRequest(requestConfig)
    && getRequestAuthHeader(requestConfig)
  ) {
    try {
      const refreshedToken = await sessionRefreshHandler(requestConfig);
      if (refreshedToken) {
        requestConfig.headers = requestConfig.headers || {};
        requestConfig.headers.Authorization = `Bearer ${refreshedToken}`;
      }
    } catch (error) {
      if (unauthorizedHandler) {
        unauthorizedHandler({
          ...error,
          config: requestConfig,
        });
      }
      return Promise.reject(error);
    }
  }
  return requestConfig;
});

export function setUnauthorizedHandler(handler) {
  unauthorizedHandler = typeof handler === 'function' ? handler : null;
}

export function setSessionRefreshHandler(handler) {
  sessionRefreshHandler = typeof handler === 'function' ? handler : null;
}

export function setAuthToken(token) {
  if (token) {
    apiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    delete apiClient.defaults.headers.common.Authorization;
  }
}

export function clearAuthToken() {
  delete apiClient.defaults.headers.common.Authorization;
}

export default apiClient;
